import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Product } from "./products";
import { analytics } from "./analytics";

type CartItem = { product: Product; qty: number };

type CartCtx = {
  items: CartItem[];
  add: (p: Product, qty?: number) => void;
  remove: (slug: string) => void;
  setQty: (slug: string, qty: number) => void;
  clear: () => void;
  count: number;
  subtotal: number;
};

const Ctx = createContext<CartCtx | null>(null);
const KEY = "kakaiys.cart.v1";

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const value = useMemo<CartCtx>(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.qty * i.product.price, 0);
    return {
      items,
      count,
      subtotal,
      add: (p, qty = 1) => {
        analytics.addToCart({
          slug: p.slug,
          name: p.name,
          price: p.price,
          category: (p as { category?: string }).category,
          quantity: qty,
        });
        setItems((prev) => {
          const found = prev.find((i) => i.product.slug === p.slug);
          if (found)
            return prev.map((i) =>
              i.product.slug === p.slug ? { ...i, qty: i.qty + qty } : i,
            );
          return [...prev, { product: p, qty }];
        });
      },
      remove: (slug) =>
        setItems((prev) => {
          const found = prev.find((i) => i.product.slug === slug);
          if (found) {
            analytics.removeFromCart({
              slug: found.product.slug,
              name: found.product.name,
              price: found.product.price,
              quantity: found.qty,
            });
          }
          return prev.filter((i) => i.product.slug !== slug);
        }),
      setQty: (slug, qty) =>
        setItems((prev) =>
          prev.map((i) => (i.product.slug === slug ? { ...i, qty: Math.max(1, qty) } : i)),
        ),
      clear: () => setItems([]),
    };
  }, [items]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

export function formatPrice(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}
